/**
 * Product image gallery.
 *
 * Desktop (>=768px): vertical thumbnails + a single main image that
 *   cross-fades between slides (slides are stacked, opacity-toggled).
 * Mobile (<768px): the main image becomes a native horizontal scroll-snap
 *   carousel with a peek of the next image and pagination dots below.
 *   Thumbnails are hidden; the active dot tracks the scroll position.
 *
 * The same `is-active` class drives the desktop crossfade, the thumbnails,
 * and the dots, so selection stays in sync across all controls and with
 * variant changes.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-image-slide]')];
    this.thumbnails = [...this.querySelectorAll('[data-thumbnail]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.mainImage = this.querySelector('[data-main-image]');
    this.mobile = window.matchMedia('(max-width: 767px)');

    if (this.slides.length <= 1) return;

    this.thumbnails.forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(thumb.dataset.target))
    );
    this.dots.forEach((dot) =>
      dot.addEventListener('click', () => this.goTo(dot.dataset.dotTarget))
    );

    this.#initScrollSync();
    this.#initSwipe();
    this.#initVariantSync();

    // Apply the initial dot window around the first (active) slide.
    this.#setActive(this.slides[this.currentIndex].dataset.imageId);
  }

  get currentIndex() {
    const i = this.slides.findIndex((s) => s.classList.contains('is-active'));
    return i < 0 ? 0 : i;
  }

  #setActive(imageId) {
    const id = String(imageId);
    const activeIndex = this.slides.findIndex((s) => s.dataset.imageId === id);

    this.slides.forEach((slide) =>
      slide.classList.toggle('is-active', slide.dataset.imageId === id)
    );
    this.thumbnails.forEach((thumb) => {
      const isActive = thumb.dataset.target === id;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-selected', isActive);
    });
    // Dots: active state + a sliding window so only a handful show at once.
    // Within 1 of active = full, 2 = small, 3 = tiny, beyond = hidden.
    this.dots.forEach((dot, i) => {
      const isActive = dot.dataset.dotTarget === id;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive);
      const dist = activeIndex < 0 ? i : Math.abs(i - activeIndex);
      dot.classList.toggle('dot-sm', dist === 2);
      dot.classList.toggle('dot-xs', dist === 3);
      dot.classList.toggle('dot-hidden', dist > 3);
    });
  }

  goTo(imageId) {
    this.#setActive(imageId);
    // On the mobile carousel, scroll the chosen slide into view.
    if (this.mobile.matches && this.mainImage) {
      const slide = this.slides.find((s) => s.dataset.imageId === String(imageId));
      slide?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  }

  next() {
    const idx = (this.currentIndex + 1) % this.slides.length;
    this.goTo(this.slides[idx].dataset.imageId);
  }

  prev() {
    const idx = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goTo(this.slides[idx].dataset.imageId);
  }

  /**
   * Mobile carousel: keep the active dot in sync with the scroll position.
   * The active slide is the one whose left edge is closest to the
   * container's left edge (start snap alignment).
   */
  #initScrollSync() {
    if (!this.mainImage) return;
    let raf = null;
    this.mainImage.addEventListener(
      'scroll',
      () => {
        if (!this.mobile.matches) return;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const cLeft = this.mainImage.getBoundingClientRect().left;
          let best = this.slides[0];
          let bestDist = Infinity;
          this.slides.forEach((slide) => {
            const dist = Math.abs(slide.getBoundingClientRect().left - cLeft);
            if (dist < bestDist) {
              bestDist = dist;
              best = slide;
            }
          });
          if (best) this.#setActive(best.dataset.imageId);
        });
      },
      { passive: true }
    );
  }

  /**
   * Desktop crossfade swipe. On mobile the browser handles swiping natively
   * via scroll-snap, so this is a no-op there.
   */
  #initSwipe() {
    let startX = 0;

    this.mainImage?.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
      },
      { passive: true }
    );

    this.mainImage?.addEventListener(
      'touchend',
      (e) => {
        if (this.mobile.matches) return;
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
          diff > 0 ? this.next() : this.prev();
        }
      },
      { passive: true }
    );
  }

  #initVariantSync() {
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail?.variant;
      if (variant?.featured_image) this.goTo(variant.featured_image.id);
    });
  }
}

customElements.define('product-gallery', ProductGallery);

export { ProductGallery };
